import React from 'react';
import { WfnAvatar } from '@adp-wfn/mdf-wc-react';
import { IconName } from '@waypoint/ui-framework/dist/typescript/icon-name';
import { iconMapper } from '../util/iconMapper';

// Size comparison between legacy Avatar and WfnAvatar (Waypoint):
//
//   Avatar2 size | Legacy Avatar | WfnAvatar  | Pixels
//   -------------|---------------|------------|-------
//   2xs          | (none)        | 2xs        | 24px
//   xs           | (none)        | xs         | 32px
//   sm           | sm - 40px     | sm         | 40px
//   md           | md - 70px     | lg  (mapped for compatibility)  | 64px
//   new-md       | (none)        | md         | 48px
//   lg           | lg - 100px    | xl  (mapped for compatibility)  | 96px
//   new-lg       | (none)        | lg         | 64px
//   xl           | xl - 125px    | xl  (no exact match, closest)   | 96px

export type IAvatar2Size = '2xs' | 'xs' | 'sm' | 'md' | 'new-md' | 'lg' | 'new-lg' | 'xl';

type WfnAvatarSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const sizeMap: Record<IAvatar2Size, WfnAvatarSize> = {
  '2xs': '2xs',
  'xs': 'xs',
  'sm': 'sm',
  'md': 'lg',
  'new-md': 'md',
  'lg': 'xl',
  'new-lg': 'lg',
  'xl': 'xl'
};

export interface IAvatar2Props {
  /**
   * The CSS class to add to the Avatar.
   */
  className?: string;

  /**
   * The Waypoint icon to show when there are no initials and no valid src.
   * Defaults to 'user'.
   */
  icon?: IconName;

  /**
   * The initials to show in the Avatar if there is no URL or an error fetching the URL.
   */
  initials?: string;

  /**
   * The size of the Avatar. Defaults to 'sm'.
   *
   * Legacy sizes are mapped to the closest Waypoint equivalent:
   *   - 'sm' (40px) -> Waypoint 'sm' (40px) - exact match
   *   - 'md' (70px) -> Waypoint 'lg' (64px) - closest match
   *   - 'lg' (100px) -> Waypoint 'xl' (96px) - closest match
   *   - 'xl' (125px) -> Waypoint 'xl' (96px) - closest available
   *
   * Use 'new-md' (48px) or 'new-lg' (64px) to access Waypoint sizes directly.
   */
  size?: IAvatar2Size;

  /**
   * The style object to add to the Avatar.
   */
  style?: React.CSSProperties;

  /**
   * The URL for the Avatar image. Can be a `data:image` value or a URL.
   * Maps to the WfnAvatar `src` property.
   */
  url?: string;
}

// Avatar2 is a compatibility wrapper around the WfnAvatar component,
// accepting the same props as the legacy Avatar from @synerg/vdl-react-components.
// WfnAvatar is used instead of SdfAvatar because it uses background-image CSS
// rather than an <img> tag, which avoids distortion of non-square images (WFNMDFP-4427).
export const Avatar2 = (props: IAvatar2Props) => {
  const {
    className,
    icon,
    initials,
    size = 'sm',
    style,
    url
  } = props;

  // Map any legacy icon classes (e.g. "fa fa-graduation-cap") found in className to a Waypoint icon,
  // and strip those classes so only layout classes remain.
  const mappedIcon = icon ?? iconMapper(className);
  const layoutClasses = className?.split(' ')
    .filter((name) => !(name === 'fa' || name.includes('fa-') || name.includes('icon-')))
    .join(' ') || undefined;

  return (
    <WfnAvatar
      className={layoutClasses}
      icon={mappedIcon}
      initials={initials}
      size={sizeMap[size]}
      src={url}
      style={style}
    />
  );
};

Avatar2.displayName = 'Avatar2';
